import React, { useMemo } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { DAILY_QUOTES } from "@/i18n/dailyQuotes";

// Dias corridos desde a época Unix -> percorre TODO o banco de frases ao longo dos anos;
// como o banco é entrelacado por autor, dois dias seguidos nunca repetem o mesmo autor.
const dailyIndex = () => Math.floor(Date.now() / 86400000) % DAILY_QUOTES.length;

export const EditorialMarquee = () => {
  const { t, lang } = useLanguage();
  const words = t("marquee") || [];

  const quote = useMemo(() => DAILY_QUOTES[dailyIndex()], []);

  const text = quote.text[lang] || quote.text.pt;

  return (
    <section
      className="relative py-20 md:py-28 border-y border-border bg-secondary/40 overflow-hidden"
      data-testid="daily-quote"
    >
      {/* Decorative moving keywords behind the quote */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.08] flex items-center">
        <Marquee speed={22} gradient={false} autoFill>
          {words.map((w, i) => (
            <span key={i} className="flex items-center">
              <span className="font-serif italic text-6xl sm:text-8xl text-foreground px-14">
                {w}
              </span>
              <span className="marquee-dot" />
            </span>
          ))}
        </Marquee>
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-6 md:px-10 text-center">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-sans uppercase tracking-[0.28em] text-[11px] font-semibold text-primary mb-8"
          data-testid="daily-quote-overline"
        >
          {t("dailyquote.overline")}
        </motion.p>

        <motion.blockquote
          key={text}
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif italic text-2xl sm:text-3xl md:text-[2.5rem] leading-[1.25] text-foreground max-w-3xl mx-auto"
          data-testid="daily-quote-text"
        >
          <span aria-hidden className="text-primary/60 font-serif not-italic mr-1">“</span>
          {text}
          <span aria-hidden className="text-primary/60 font-serif not-italic ml-1">”</span>
        </motion.blockquote>

        <motion.footer
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 flex items-center justify-center gap-3"
        >
          <span className="h-px w-8 bg-primary/50" />
          <span className="font-sans uppercase tracking-[0.2em] text-xs font-semibold text-foreground/80" data-testid="daily-quote-author">
            {quote.author}
            {quote.year && <span className="text-muted-foreground font-normal"> · {quote.year}</span>}
          </span>
          <span className="h-px w-8 bg-primary/50" />
        </motion.footer>
      </div>
    </section>
  );
};
